import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { all_routes } from "../../router/all_routes";
import { getNocOnlineCourse } from "../../../core/data/redux/nocOnlineCoursesSlice";
import {
  getReimbursementRequestTypes,
  getReimbursementDocumentTypes,
  createOrUpdateNocReimbursement,
} from "../../../core/data/redux/nocReimbursementSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS, BASE_URLS } from "../../../core/data/redux/apis/urls";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";

interface UploadedDocItem {
  fileName: string;
  fileExtension: string;
  fileSize: string;
  fileType: string;
  serverKey: string;
  serverURL: string;
  documentType: string;
  typeOfAttachedDoc: string;
  empId?: string;
  documentUploadedBy?: string;
  originalName?: string;
}

const NOCOnlineCourseReimbursement: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();

  // Redux state
  const { currentCourse, loading: courseLoading } = useSelector(
    (state: RootState) => state.nocOnlineCoursesState
  );
  const { requestTypes, documentTypes, createLoading } = useSelector(
    (state: RootState) => state.nocReimbursementState
  );
  const employeesState = useSelector((state: RootState) => state.employeesState);
  const user = employeesState?.data?.data;

  // Form State
  const [requestType, setRequestType] = useState<string>("CL");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [requestAmount, setRequestAmount] = useState<string>("");
  const [dOJOnlineCourse, setDOJOnlineCourse] = useState<string>("");
  const [courseCompletionDate, setCourseCompletionDate] = useState<string>("");
  const [durationOfOnlineCourse, setDurationOfOnlineCourse] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [declarationChecked, setDeclarationChecked] = useState<boolean>(true);

  // Document Uploads State
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocItem[]>([]);
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);

  // Validation Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Duration options: 3 to 15 weeks
  const durationOptions = useMemo(() => {
    return Array.from({ length: 13 }, (_, i) => ({
      id: `${i + 3}`,
      label: `${i + 3} Weeks`,
    }));
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (id) {
      dispatch(getNocOnlineCourse({ id: parseInt(id) }));
    }
    dispatch(getMyProfile({}));
    dispatch(getReimbursementRequestTypes());
    dispatch(getReimbursementDocumentTypes());
  }, [id, dispatch]);

  // Prepopulate contact information
  useEffect(() => {
    if (user?.details) {
      setContactNumber(user.details.phone || user.details.mobileNumber || "");
      setEmail(user.details.email || "");
    }
  }, [user]);

  // Request types list with fallback
  const requestTypeList = useMemo(() => {
    if (requestTypes && Array.isArray(requestTypes) && requestTypes.length > 0) {
      return requestTypes.map((rt: any) => ({
        id: rt.type_id || rt.id || rt.type_name,
        title:
          rt.type_title ||
          rt.type_name ||
          (rt.type_id === "CL"
            ? "Reimbursement and Addition of record"
            : "Only Addition of record in System"),
      }));
    }
    return [
      { id: "CL", title: "Reimbursement and Addition of record" },
      { id: "AD", title: "Only Addition of record in System" },
    ];
  }, [requestTypes]);

  // Document types mapping with fallback
  const docTypesList = useMemo(() => {
    if (documentTypes && Array.isArray(documentTypes) && documentTypes.length > 0) {
      return documentTypes.map((dt: any) => ({
        id: String(dt.type_id || dt.id),
        title: dt.type_title || dt.type_name,
      }));
    }
    return [
      { id: "01", title: "Fee Receipt" },
      { id: "02", title: "Proof of Payment in INR" },
      { id: "03", title: "Course Completion Certificate" },
    ];
  }, [documentTypes]);

  const formatDateDisplay = (dateString: string | undefined | null) => {
    if (!dateString || dateString === "-" || dateString === "0000-00-00" || dateString === "00-00-0000") return "-";
    try {
      const d = dayjs(dateString);
      return d.isValid() ? d.format("DD-MM-YYYY") : dateString;
    } catch {
      return dateString;
    }
  };

  // Convert File to Base64 (Data URL or base64)
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Handle File Upload to AWS
  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    typeId: string
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must not exceed 2MB.");
      return;
    }

    try {
      setUploadingDocType(typeId);
      const base64Content = await fileToBase64(file);
      const fileExt = file.name.split(".").pop() || "";
      const docTypeTitle =
        docTypesList.find((d) => d.id === typeId)?.title || "Upload Documents";

      const empId = user?.details?.empId || (currentCourse as any)?.empId || "";

      const uploadPayload = {
        filestring: base64Content,
      };

      const response: any = await Api.post(
        API_URLS.AWS_BUCKET.UPLOAD_FILE,
        uploadPayload
      );

      const resData = response?.data?.data || response?.data || response;
      const serverKey =
        resData?.key || resData?.serverKey || resData?.UUID || resData?.fileKey || "";
      const serverURL = resData?.url || resData?.serverURL || "";

      if (!serverKey && !serverURL) {
        throw new Error(
          response?.data?.message ||
          response?.message ||
          "Failed to get uploaded file details"
        );
      }

      const newDoc: UploadedDocItem = {
        fileName: serverKey || file.name,
        originalName: file.name,
        fileExtension: fileExt,
        fileSize: (file.size / 1024).toFixed(2) + " KB",
        fileType: file.type || fileExt,
        serverKey,
        serverURL,
        documentType: "Upload Documents",
        typeOfAttachedDoc: typeId,
        empId,
        documentUploadedBy: empId,
      };

      setUploadedDocs((prev) => {
        const filtered = prev.filter((d) => d.typeOfAttachedDoc !== typeId);
        return [...filtered, newDoc];
      });

      // Clear document error
      if (typeId === "01") setErrors((prev) => ({ ...prev, feeReceipt: "" }));
      if (typeId === "03") setErrors((prev) => ({ ...prev, completionCert: "" }));

      toast.success(`${docTypeTitle} uploaded successfully!`);
    } catch (err: any) {
      console.error("Upload error:", err);
      toast.error(
        Api.getErrorMessage(err) || "Failed to upload document. Please try again."
      );
    } finally {
      setUploadingDocType(null);
      e.target.value = "";
    }
  };

  const handleDeleteDoc = (typeId: string) => {
    setUploadedDocs((prev) => prev.filter((d) => d.typeOfAttachedDoc !== typeId));
    toast.info("Attachment removed.");
  };

  const handleViewDoc = async (doc: UploadedDocItem) => {
    if (doc.serverURL && (doc.serverURL.startsWith("http://") || doc.serverURL.startsWith("https://"))) {
      window.open(doc.serverURL, "_blank");
      return;
    }
    if (doc.serverKey) {
      try {
        const res: any = await Api.post(API_URLS.AWS_BUCKET.GET_PRESIGNED_URL, {
          key: doc.serverKey,
        });
        const pUrl = res?.data?.data?.url || res?.data?.url || res?.data;
        if (pUrl && typeof pUrl === "string") {
          window.open(pUrl, "_blank");
        } else {
          toast.error("Unable to generate file preview link.");
        }
      } catch {
        toast.error("Failed to load document.");
      }
    }
  };

  const getUploadedDocByType = (typeId: string) => {
    return uploadedDocs.find((d) => d.typeOfAttachedDoc === typeId);
  };

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!requestType) {
      newErrors.requestType = "Request type is required.";
    }

    if (requestType === "CL") {
      const amt = parseFloat(requestAmount);
      if (!requestAmount || isNaN(amt) || amt <= 0) {
        newErrors.requestAmount = "Requested amount is required and must be greater than 0.";
      } else if (amt > 18000) {
        newErrors.requestAmount = "Requested amount cannot exceed ₹18,000 for the block.";
      }
    }

    if (!dOJOnlineCourse) {
      newErrors.dOJOnlineCourse = "Date of joining course is required.";
    }

    if (!courseCompletionDate) {
      newErrors.courseCompletionDate = "Date of completion of course is required.";
    } else if (dOJOnlineCourse && dayjs(courseCompletionDate).isBefore(dayjs(dOJOnlineCourse))) {
      newErrors.courseCompletionDate = "Completion date cannot be before date of joining.";
    }

    if (!durationOfOnlineCourse) {
      newErrors.durationOfOnlineCourse = "Duration of course is required.";
    }

    if (!contactNumber || contactNumber.length < 10) {
      newErrors.contactNumber = "Please enter a valid 10-digit phone number.";
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Attachment validation
    if (requestType !== "AD" && !getUploadedDocByType("01")) {
      newErrors.feeReceipt = "Fee receipt document is mandatory.";
    }

    if (!getUploadedDocByType("03")) {
      newErrors.completionCert = "Course completion certificate is mandatory.";
    }

    if (!declarationChecked) {
      newErrors.declaration = "You must accept the declaration to proceed.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all mandatory fields correctly.");
      return;
    }

    if (!currentCourse) {
      toast.error("Associated NOC details not loaded. Please try again.");
      return;
    }

    const payload = {
      claimOrOnlyAdditi: requestType,
      referenceNumber1: currentCourse.referenceNumber,
      requestAmount: requestType === "CL" ? requestAmount : "0.00",
      dOJOnlineCourse: dOJOnlineCourse,
      courseCompletionDate: courseCompletionDate,
      durationOfOnlineCourse: durationOfOnlineCourse,
      remarks: remarks || "",
      contactNumber: contactNumber,
      email: email,
      is_draft: false,
      documents: uploadedDocs.map((doc) => ({
        fileName: doc.fileName,
        fileExtension: doc.fileExtension,
        fileSize: doc.fileSize,
        fileType: doc.fileType,
        serverKey: doc.serverKey,
        serverURL: doc.serverURL,
        documentType: doc.documentType,
        typeOfAttachedDoc: doc.typeOfAttachedDoc,
        empId: doc.empId,
        documentUploadedBy: doc.documentUploadedBy || "EMPLOYEE",
      })),
    };

    try {
      await dispatch(createOrUpdateNocReimbursement(payload)).unwrap();
      toast.success("Application for Addition/Reimbursement submitted successfully!");
      navigate(routes.noc.onlineCourse);
    } catch (err: any) {
      console.error("Submission error:", err);
      toast.error(
        Api.getErrorMessage(err) || "Failed to submit reimbursement application."
      );
    }
  };

  if (courseLoading && !currentCourse) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading associated NOC details...</p>
        </div>
      </div>
    );
  }

  const anyCourse = currentCourse as any;
  const courseCategory = anyCourse?.course_category || {};
  const courseDept =
    courseCategory?.course_departments ||
    anyCourse?.course_departments ||
    {};

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.onlineCourse}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                Application for Addition or Reimbursement
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.onlineCourse}>Online Course</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Application for Addition or Reimbursement
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Top Section: Employee & Associated NOC fields in individual boxes matching mobile */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                {/* 1. Associated Reference No. */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Associated Reference No.
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={currentCourse?.referenceNumber || "-"}
                    disabled
                    readOnly
                  />
                </div>

                {/* 2. Name */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Name
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      user?.details
                        ? `${user.details.firstname || ""} ${user.details.lastname || user.details.lsastname || ""}`.trim()
                        : "-"
                    }
                    disabled
                    readOnly
                  />
                </div>

                {/* 3. Designation */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Designation
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      user?.details?.sap_department_designation_wings?.designation_name ||
                      user?.details?.designation_details?.longText ||
                      "-"
                    }
                    disabled
                    readOnly
                  />
                </div>

                {/* 4. Date of Joining */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Date of Joining
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDateDisplay(user?.details?.date_of_joining)}
                    disabled
                    readOnly
                  />
                </div>

                {/* 5. Employment Status */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Employment Status
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={user?.details?.employmentType || "Regular"}
                    disabled
                    readOnly
                  />
                </div>

                {/* 6. Department */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Department
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      user?.details?.sap_department_designation_wings?.department_name ||
                      user?.details?.department_details?.departmentName ||
                      "-"
                    }
                    disabled
                    readOnly
                  />
                </div>

                {/* 7. Phone Number */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    className={`form-control ${errors.contactNumber ? "is-invalid" : ""}`}
                    placeholder="Enter 10-digit mobile number"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, ""))}
                  />
                  {errors.contactNumber && (
                    <div className="invalid-feedback">{errors.contactNumber}</div>
                  )}
                </div>

                {/* 8. Email */}
                <div className="col-md-6 col-lg-3">
                  <label className="form-label text-dark fw-bold small">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section: Course details as per HR Compendium */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="card-title mb-0 fw-bold text-dark">
                Course details as per HR Compendium
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6 col-lg-4">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Category:</small>
                    <div className="fw-medium text-dark">
                      {courseCategory?.category_title || "Specialized Course"}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-4">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Department:</small>
                    <div className="fw-medium text-dark">
                      {courseDept?.department || anyCourse?.onlineCourseDepartment || "-"}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-4">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Course Name:</small>
                    <div className="fw-bold text-dark">
                      {anyCourse?.onlineCourseName || courseDept?.course || "-"}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Platform:</small>
                    <div className="fw-medium text-dark">
                      {courseDept?.plateform || "-"}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Duration:</small>
                    <div className="fw-medium text-dark">
                      {courseDept?.duration || "-"}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Online Course Fee:</small>
                    <div className="fw-bold text-dark">
                      {courseDept?.course_fee || anyCourse?.courseFee || "0.00"}
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-lg-3">
                  <div className="p-3 bg-light rounded border h-100">
                    <small className="text-muted d-block fw-bold mb-1">Contributor:</small>
                    <div className="fw-medium text-dark">
                      {courseDept?.contributor || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Previous Reimbursements for Online Courses in Tabular Form */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ti ti-briefcase me-2 text-primary fs-18"></i>
                <h5 className="card-title mb-0 fw-bold text-dark">
                  Previous Reimbursements for Online Courses
                </h5>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-bordered table-striped mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-nowrap">Category</th>
                      <th className="text-nowrap">Date of joining course</th>
                      <th className="text-nowrap">Platform</th>
                      <th className="text-nowrap">Course duration</th>
                      <th className="text-nowrap">Course Name</th>
                      <th className="text-nowrap">Completion Date</th>
                      <th className="text-nowrap">Online Course Fee</th>
                      <th className="text-nowrap">Reimbursement Date</th>
                      <th className="text-nowrap">End of Block</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((currentCourse as any)?.previous_reimbursements?.length || 0) > 0 ? (
                      (currentCourse as any).previous_reimbursements.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td>{item.category || item.category_title || "-"}</td>
                          <td>{formatDateDisplay(item.dateOfJoining || item.dOJOnlineCourse)}</td>
                          <td>{item.platform || item.plateform || "-"}</td>
                          <td>{item.duration || item.durationOfOnlineCourse || "-"}</td>
                          <td className="fw-medium">{item.courseName || item.onlineCourseName || "-"}</td>
                          <td>{formatDateDisplay(item.dateOfCompletion || item.courseCompletionDate)}</td>
                          <td>{item.onlineCourseFee ? `₹${item.onlineCourseFee}` : "-"}</td>
                          <td>{formatDateDisplay(item.dateOfReimbursement || item.created_at)}</td>
                          <td>{formatDateDisplay(item.endOfBlock || item.endOfBlockDate)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center py-4 bg-light">
                          <div className="py-2 text-muted fw-medium d-flex align-items-center justify-content-center gap-2">
                            <i className="ti ti-info-circle text-warning fs-18"></i>
                            <span>No reimbursement taken for Online course previously</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Note text exactly from mobile */}
          <div className="mb-3 px-1">
            <span className="text-dark fw-medium small">
              Note: If the fee is in dollars/other currency, please provide the amount in INR<span className="text-danger">*</span>
            </span>
          </div>

          {/* Form Fields Card */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3">
                {/* Request Type* */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark">
                    Request Type<span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.requestType ? "is-invalid" : ""}`}
                    value={requestType}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRequestType(val);
                      if (val !== "CL") {
                        setRequestAmount("0.00");
                      }
                    }}
                  >
                    {requestTypeList.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                  {errors.requestType && (
                    <div className="invalid-feedback">{errors.requestType}</div>
                  )}
                </div>

                {/* Block Year ending on* */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark">
                    Block Year ending on<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={formatDateDisplay(anyCourse?.endOfBlockDate) || "31-03-2027"}
                    disabled
                    readOnly
                  />
                </div>

                {/* Amount Remaining for Current Block Year (out of ₹18,000)* */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark">
                    Amount Remaining for Current Block Year (out of ₹18,000)<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={anyCourse?.eligibilityAmount || "18000"}
                    disabled
                    readOnly
                  />
                </div>

                {/* Requested Amount* (Only when CL) */}
                {requestType === "CL" && (
                  <div className="col-12">
                    <label className="form-label fw-bold text-dark">
                      Requested Amount<span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      maxLength={6}
                      min={1}
                      className={`form-control ${errors.requestAmount ? "is-invalid" : ""}`}
                      placeholder="Enter Amount"
                      value={requestAmount}
                      onChange={(e) => setRequestAmount(e.target.value)}
                    />
                    <small className="text-muted d-block mt-1">
                      Note: If the fee is in dollars/other currency, please provide the amount in INR<span className="text-danger">*</span>
                    </small>
                    {errors.requestAmount && (
                      <div className="invalid-feedback d-block">{errors.requestAmount}</div>
                    )}
                  </div>
                )}

                {/* Date of Joining Online Course* */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark">
                    Date of Joining Online Course<span className="text-danger">*</span>
                  </label>
                  <ConfigProvider>
                    <DatePicker
                      className={`form-control ${errors.dOJOnlineCourse ? "is-invalid" : ""}`}
                      format="DD-MM-YYYY"
                      placeholder="DD-MM-YYYY"
                      style={{ width: "100%" }}
                      value={dOJOnlineCourse ? dayjs(dOJOnlineCourse) : null}
                      onChange={(date) => {
                        setDOJOnlineCourse(date ? date.format("YYYY-MM-DD") : "");
                      }}
                    />
                  </ConfigProvider>
                  {errors.dOJOnlineCourse && (
                    <div className="invalid-feedback d-block">{errors.dOJOnlineCourse}</div>
                  )}
                </div>

                {/* Date of Completion of Course* */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark">
                    Date of Completion of Course<span className="text-danger">*</span>
                  </label>
                  <ConfigProvider>
                    <DatePicker
                      className={`form-control ${errors.courseCompletionDate ? "is-invalid" : ""}`}
                      format="DD-MM-YYYY"
                      placeholder="DD-MM-YYYY"
                      style={{ width: "100%" }}
                      value={courseCompletionDate ? dayjs(courseCompletionDate) : null}
                      onChange={(date) => {
                        setCourseCompletionDate(date ? date.format("YYYY-MM-DD") : "");
                      }}
                    />
                  </ConfigProvider>
                  {errors.courseCompletionDate && (
                    <div className="invalid-feedback d-block">{errors.courseCompletionDate}</div>
                  )}
                </div>

                {/* Duration of Course* */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark">
                    Duration of Course<span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.durationOfOnlineCourse ? "is-invalid" : ""}`}
                    value={durationOfOnlineCourse}
                    onChange={(e) => setDurationOfOnlineCourse(e.target.value)}
                  >
                    <option value="">Select Duration</option>
                    {durationOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {errors.durationOfOnlineCourse && (
                    <div className="invalid-feedback">{errors.durationOfOnlineCourse}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section: Instructions & Attachments */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="card-title mb-0 fw-bold text-dark">
                Instructions & Attachments
              </h5>
            </div>
            <div className="card-body">
              {/* Instructions list from mobile */}
              <div className="mb-4">
                <h6 className="fw-bold text-dark mb-2">Instructions</h6>
                <ul className="list-unstyled text-dark small ps-1 mb-0">
                  {requestType !== "AD" && (
                    <>
                      <li className="mb-1">• Fee Receipt (for Reimbursement and Addition both)</li>
                      <li className="mb-1">• Proof of payment in INR (if fee receipt is in another currency)</li>
                    </>
                  )}
                  <li className="mb-1">• Completion Certificate</li>
                </ul>
              </div>

              <div className="row g-4">
                {/* Upload Fee Receipt* (Only for CL) */}
                {requestType !== "AD" && (
                  <div className="col-12">
                    <label className="form-label fw-bold text-dark d-block">
                      Upload Fee Receipt<span className="text-danger">*</span>
                    </label>
                    {getUploadedDocByType("01") ? (
                      <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                        <div className="d-flex align-items-center text-truncate me-2">
                          <i className="ti ti-file-text text-danger fs-22 me-2"></i>
                          <span className="text-truncate fw-medium text-dark">
                            {getUploadedDocByType("01")?.originalName || getUploadedDocByType("01")?.fileName}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewDoc(getUploadedDocByType("01")!)}
                            title="Preview File"
                          >
                            <i className="ti ti-eye"></i> View
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteDoc("01")}
                            title="Delete File"
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="input-group">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            id="file_input_01"
                            className="form-control"
                            style={{ display: "none" }}
                            disabled={uploadingDocType === "01"}
                            onChange={(e) => handleFileUpload(e, "01")}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center gap-2 flex-grow-1 text-start"
                            onClick={() => document.getElementById("file_input_01")?.click()}
                            disabled={uploadingDocType === "01"}
                          >
                            <i className="ti ti-paperclip text-danger fs-18"></i>
                            <span className="text-muted">Choose File</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary d-flex align-items-center gap-1 px-4"
                            onClick={() => document.getElementById("file_input_01")?.click()}
                            disabled={uploadingDocType === "01"}
                          >
                            <i className="ti ti-cloud-upload"></i>
                            <span>{uploadingDocType === "01" ? "Uploading..." : "Upload"}</span>
                          </button>
                        </div>
                        {errors.feeReceipt && (
                          <div className="text-danger small mt-1">{errors.feeReceipt}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Proof of Payment in INR (Only for CL) */}
                {requestType !== "AD" && (
                  <div className="col-12">
                    <label className="form-label fw-bold text-dark d-block">
                      Upload Proof of Payment in INR
                    </label>
                    {getUploadedDocByType("02") ? (
                      <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                        <div className="d-flex align-items-center text-truncate me-2">
                          <i className="ti ti-file-text text-primary fs-22 me-2"></i>
                          <span className="text-truncate fw-medium text-dark">
                            {getUploadedDocByType("02")?.originalName || getUploadedDocByType("02")?.fileName}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewDoc(getUploadedDocByType("02")!)}
                            title="Preview File"
                          >
                            <i className="ti ti-eye"></i> View
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteDoc("02")}
                            title="Delete File"
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="input-group">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            id="file_input_02"
                            className="form-control"
                            style={{ display: "none" }}
                            disabled={uploadingDocType === "02"}
                            onChange={(e) => handleFileUpload(e, "02")}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center gap-2 flex-grow-1 text-start"
                            onClick={() => document.getElementById("file_input_02")?.click()}
                            disabled={uploadingDocType === "02"}
                          >
                            <i className="ti ti-paperclip text-primary fs-18"></i>
                            <span className="text-muted">Choose File</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary d-flex align-items-center gap-1 px-4"
                            onClick={() => document.getElementById("file_input_02")?.click()}
                            disabled={uploadingDocType === "02"}
                          >
                            <i className="ti ti-cloud-upload"></i>
                            <span>{uploadingDocType === "02" ? "Uploading..." : "Upload"}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Completion Certificate* (Mandatory) */}
                <div className="col-12">
                  <label className="form-label fw-bold text-dark d-block">
                    Upload Completion Certificate<span className="text-danger">*</span>
                  </label>
                  {getUploadedDocByType("03") ? (
                    <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded border">
                      <div className="d-flex align-items-center text-truncate me-2">
                        <i className="ti ti-file-text text-primary fs-22 me-2"></i>
                        <span className="text-truncate fw-medium text-dark">
                          {getUploadedDocByType("03")?.originalName || getUploadedDocByType("03")?.fileName}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleViewDoc(getUploadedDocByType("03")!)}
                          title="Preview File"
                        >
                          <i className="ti ti-eye"></i> View
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteDoc("03")}
                          title="Delete File"
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="input-group">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          id="file_input_03"
                          className="form-control"
                          style={{ display: "none" }}
                          disabled={uploadingDocType === "03"}
                          onChange={(e) => handleFileUpload(e, "03")}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 flex-grow-1 text-start"
                          onClick={() => document.getElementById("file_input_03")?.click()}
                          disabled={uploadingDocType === "03"}
                        >
                          <i className="ti ti-paperclip text-primary fs-18"></i>
                          <span className="text-muted">Choose File</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center gap-1 px-4"
                          onClick={() => document.getElementById("file_input_03")?.click()}
                          disabled={uploadingDocType === "03"}
                        >
                          <i className="ti ti-cloud-upload"></i>
                          <span>{uploadingDocType === "03" ? "Uploading..." : "Upload"}</span>
                        </button>
                      </div>
                      {errors.completionCert && (
                        <div className="text-danger small mt-1">{errors.completionCert}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Any Remarks */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body">
              <label className="form-label fw-bold text-dark">Any Remarks</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="Enter Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              ></textarea>

              {/* Bottom Note exactly matching mobile */}
              <div className="mt-3 text-muted small">
                NOTE: Reimbursement request should be applied within 6 months of completion of the course.
              </div>
            </div>
          </div>

          {/* Declaration Checkbox */}
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-body">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="declaration_checkbox"
                  checked={declarationChecked}
                  onChange={(e) => setDeclarationChecked(e.target.checked)}
                />
                <label
                  htmlFor="declaration_checkbox"
                  className="form-check-label text-dark fw-medium small"
                >
                  I hereby declare that all the information provided above is true and correct to the best of my knowledge.
                </label>
              </div>
              {errors.declaration && (
                <div className="text-danger small mt-1">{errors.declaration}</div>
              )}
            </div>
          </div>

          {/* Action Buttons: Cancel + Submit */}
          <div className="d-flex align-items-center justify-content-end gap-3 mb-5">
            <Link
              to={routes.noc.onlineCourse}
              className="btn btn-outline-secondary px-4 py-2"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-primary px-5 py-2 fw-semibold"
              disabled={createLoading}
            >
              {createLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Submitting Application...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NOCOnlineCourseReimbursement;
